import { FC, useMemo, useState } from 'react';

import { Box, Button, Center, ShareOutline, SparklesOutline, Spinner, Text, VStack } from '@bonusly/design-system';

import { useSpotlight } from '../../queries/spotlight';
import { SpotlightCard } from './spotlight-card';
import { SpotlightNavigation } from './spotlight-navigation';

const BackgroundGradient = () => {
	const gradientId = useMemo(() => `paint0_linear_${Math.random().toString(36).substr(2, 9)}`, []);

	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			width="100vw"
			height="100vh"
			zIndex={0}
			overflow="hidden"
			pointerEvents="none"
			bgGradient="linear(to-r, #D1FAE5, #FEF3C7)"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 1213 920"
				preserveAspectRatio="xMidYMid slice"
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					minWidth: '100vw',
					minHeight: '100vh',
				}}
			>
				<path
					d="M1146.6 415.325C1316.71 697.376 1116.16 984.089 994.617 1092.19C994.617 1092.19 589.215 1521.96 345.106 1438.04C100.996 1354.13 -569.564 527.855 -237.865 192.536C143.587 -193.081 933.964 62.7607 1146.6 415.325Z"
					fill={`url(#${gradientId})`}
				/>
				<defs>
					<linearGradient id={gradientId} x1="-63.0156" y1="38.4885" x2="1087.73" y2="1344.89" gradientUnits="userSpaceOnUse">
						<stop stopColor="white" stopOpacity="0.6" />
						<stop offset="0.461538" stopColor="white" stopOpacity="0.2" />
						<stop offset="1" stopColor="white" stopOpacity="0.4" />
					</linearGradient>
				</defs>
			</svg>
		</Box>
	);
};
interface SpotlightViewerProps {
	spotlightId: string;
}

export const SpotlightViewer: FC<SpotlightViewerProps> = ({ spotlightId }) => {
	const { data: spotlight, isLoading, error } = useSpotlight(spotlightId);
	const [currentIndex, setCurrentIndex] = useState(0);

	const handleNext = () => {
		if (currentIndex < (spotlight?.sections.length ?? 0)) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	const handlePrevious = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	const handleGoToCard = (index: number) => {
		setCurrentIndex(index);
	};

	const handleShare = async () => {
		console.log('share');
	};

	const renderContent = () => {
		if (isLoading || !spotlight) {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Spinner size="xl" color="success.icon" />
						<Text fontSize="lg" color="text.default">
							Loading your spotlight...
						</Text>
					</VStack>
				</Center>
			);
		}

		if (error || spotlight.status === 'failed') {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Text fontSize="xl" fontWeight="semibold" color="text.default">
							Unable to load spotlight
						</Text>
						<Text fontSize="md" color="text.secondary">
							{error instanceof Error ? error.message : 'An error occurred'}
						</Text>
					</VStack>
				</Center>
			);
		}

		if (spotlight.status === 'pending' || spotlight.status === 'processing') {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Spinner size="xl" color="success.icon" />
						<Text fontSize="lg" color="text.default">
							Generating your spotlight...
						</Text>
					</VStack>
				</Center>
			);
		}

		const totalCards = spotlight.sections.length + 1; // +1 for final card
		const isFinalCard = currentIndex === spotlight.sections.length;

		return (
			<>
				{/* Progress bar */}
				<Box
					position="fixed"
					top={0}
					left={0}
					h="3px"
					bg="linear-gradient(90deg, #1DB87C, #7C5CFC, #FF6B9D)"
					width={`${((currentIndex + 1) / totalCards) * 100}%`}
					transition="width 0.4s ease"
					zIndex={100}
				/>

				{/* Main content */}
				<Box
					maxW="900px"
					mx="auto"
					px={{ base: 6, md: 12 }}
					pb={12}
					minH="100vh"
					display="flex"
					flexDirection="column"
					justifyContent="center"
					position="relative"
					zIndex={1}
				>
					<Box position="relative" width="100%" minH="750px">
						{/* Regular cards */}
						{spotlight.sections.map((section, index) => (
							<SpotlightCard key={index} section={section} isActive={currentIndex === index} />
						))}

						{/* Final card */}
						{isFinalCard && (
							<Box position="absolute" top={0} left={0} right={0} opacity={1} visibility="visible" pointerEvents="auto">
								<VStack spacing={6} align="stretch">
									<Box textAlign="center">
										<Box
											as="span"
											display="inline-block"
											px={4}
											py={2}
											borderRadius="full"
											fontSize="xs"
											fontWeight="semibold"
											textTransform="uppercase"
											letterSpacing="wide"
											mb={4}
											bg="success.bg"
											color="success.text"
											border="1px solid"
											borderColor="border.soft"
											boxShadow="0 2px 8px rgba(0, 0, 0, 0.08)"
										>
											The End
										</Box>
										<Text
											fontFamily="'Plus Jakarta Sans', sans-serif"
											fontSize="lg"
											fontWeight="medium"
											color="text.default"
											opacity={0.7}
											textTransform="uppercase"
											letterSpacing="wide"
										>
											That's a wrap on your highlights.
										</Text>
									</Box>

									<Box
										bg="bg.surface"
										backdropFilter="blur(20px)"
										borderRadius="3xl"
										p={{ base: 12, md: 16 }}
										border="2px solid"
										borderColor="bg.topNav"
										boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
										textAlign="center"
									>
										<VStack spacing={5}>
											<Box w={16} h={16} borderRadius="2xl" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={5}>
												<SparklesOutline size="lg" color="success.text" />
											</Box>
											<Text
												as="h2"
												fontFamily="'Plus Jakarta Sans', sans-serif"
												fontSize={{ base: '3xl', md: '4xl' }}
												fontWeight="bold"
												lineHeight="shorter"
												color="text.default"
											>
												You're Kind of Amazing
											</Text>
											<Text fontSize="md" color="text.secondary" mb={7}>
												Now go share your wins with the world.
											</Text>
											<Button
												leftIcon={<ShareOutline size="md" />}
												onClick={handleShare}
												bg="success.icon"
												color="text.onColor"
												size="md"
												borderRadius="full"
												px={5}
												py={3}
												fontSize="sm"
												fontWeight="semibold"
												isDisabled
												_hover={{
													bg: 'success.text',
													transform: 'scale(1.02)',
												}}
											>
												Share
											</Button>
										</VStack>
									</Box>
								</VStack>
							</Box>
						)}
					</Box>

					{/* Navigation */}
					<SpotlightNavigation
						currentIndex={currentIndex}
						totalCards={totalCards}
						onPrevious={handlePrevious}
						onNext={handleNext}
						onGoToCard={handleGoToCard}
					/>
				</Box>
			</>
		);
	};

	return (
		<Box minH="100vh" bg="bg.surface" position="relative">
			<BackgroundGradient />
			{renderContent()}
		</Box>
	);
};
